import BugReportIcon from "@mui/icons-material/BugReport";
import HistoryIcon from "@mui/icons-material/History";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import {
	Button,
	Grid,
	IconButton,
	Paper,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { formatISO } from "date-fns";
import { useEffect, useRef, useState } from "react";
import {
	getTagHistory,
	getTagsForObject,
	postTagHistory,
	putTagHistory,
	updateTags,
} from "../../api/tags";
import { TagHistory } from "../../api/tags/tag_history";
import { PersonTag, PersonTagRef } from "./Tag";
import { TagDebugDialog } from "./TagDebugDialog";
import { PersonTagHistory } from "./TagHistory";

interface TagViewProps {
	objectName: string;
	objectId: number;
	showDebug?: boolean;
}

export const TagView = ({ objectName, objectId, showDebug = false }: TagViewProps) => {
	const [tagHistory, setTagHistory] = useState<TagHistory[]>([]);
	const [tags, setTags] = useState<string[]>([]);
	const [refetchHistory, setRefetchHistory] = useState<boolean>(true);
	const [loaded, setLoaded] = useState<boolean>(false);
	const [showHistory, setShowHistory] = useState<boolean>(false);
	const [debugOpen, setDebugOpen] = useState<boolean>(false);
	const personTagRef = useRef<PersonTagRef>(null);

	const fetchTagHistory = (objectName: string, objectId: number) => {
		getTagHistory(objectName, objectId)
			.then(setTagHistory)
			.catch((err) => console.log(err));
	};

	const fetchTags = (objectName: string, objectId: number) => {
		getTagsForObject(objectName, objectId)
			.then(setTags)
			.catch((err) => console.log(err));
	};

	const saveTags = (newTags: string[]) => {
		updateTags(objectName, objectId, newTags)
			.then(() => {
				setTags(newTags);
				setRefetchHistory(true);
			})
			.catch((err) => console.log(err));
	};

	const handleTagsChange = (newTags: string[]) => {
		saveTags(newTags);
	};

	const handleRemoveTag = (tagToRemove: string) => {
		const newTags = tags.filter((t) => t !== tagToRemove);
		saveTags(newTags);
	};

	const addTagAuditLog = (tag: string, date: Date) => {
		const timestamp: string = date.toISOString();

		postTagHistory(objectName, objectId, tag, timestamp)
			.then(() => {
				setRefetchHistory(true);
			})
			.catch((err) => console.log(err));
	};

	const updateTagHistoryEntry = (
		index: number,
		tagHistoryId: number,
		date: Date | string
	) => {
		let timestamp: string = "";
		if (date instanceof Date) {
			timestamp = formatISO(date);
		} else {
			return;
		}

		putTagHistory(tagHistoryId, timestamp).catch((err) => console.log(err));
		const newArr = [...tagHistory];

		newArr.splice(index, 1, {
			...tagHistory[index],
			timestamp: timestamp,
		});

		setTagHistory(newArr);
	};

	useEffect(() => {
		if (refetchHistory) {
			fetchTagHistory(objectName, objectId);
			setRefetchHistory(false);
		}

		if (!loaded) {
			fetchTags(objectName, objectId);
			setLoaded(true);
		}
	}, [refetchHistory, objectName, objectId, loaded]);

	if (!loaded) {
		return (
			<Paper sx={{ p: 2 }} data-component="tag-view-loading">
				<Typography variant="body2">Loading...</Typography>
			</Paper>
		);
	}

	const missingHistory = tags.filter(
		(tag) => tagHistory.findIndex((el) => el.name === tag) === -1
	);

	return (
		<Paper sx={{ p: 2 }} data-component="tag-view-paper">
			<Grid
				container
				direction="column"
				spacing={1}
				data-component="tag-view-grid"
			>
				<Grid data-component="person-tag-grid">
					<PersonTag
						ref={personTagRef}
						tags={tags}
						onTagsChange={handleTagsChange}
						onRemoveTag={handleRemoveTag}
						showAddButton={false}
					/>
				</Grid>

				{/* Footer with buttons */}
				<Grid data-component="tag-footer-grid">
					<Stack
						direction="row"
						justifyContent="space-between"
						alignItems="center"
						sx={{ mt: 2 }}
						data-component="tag-footer"
					>
						{showDebug ? (
							<Tooltip title="Debug: Tags & History">
								<IconButton
									onClick={() => setDebugOpen(true)}
									size="small"
									data-component="tag-debug-button"
								>
									<BugReportIcon />
								</IconButton>
							</Tooltip>
						) : (
							<span />
						)}
						<Stack direction="row" spacing={1} alignItems="center">
							{tagHistory.length > 0 && (
								<Button
									variant="outlined"
									size="small"
									startIcon={<HistoryIcon />}
									onClick={() => setShowHistory(!showHistory)}
									data-component="toggle-history-button"
								>
									{showHistory ? "Hide History" : "Show History"}
								</Button>
							)}
							<Button
								variant="outlined"
								size="small"
								startIcon={<LocalOfferIcon />}
								onClick={() => personTagRef.current?.openDialog()}
								data-component="add-tag-button"
							>
								Add Tag
							</Button>
						</Stack>
					</Stack>
				</Grid>

				{showHistory && (
					<Grid data-component="tag-history-grid" sx={{ mt: 2 }}>
						<PersonTagHistory
							missingHistory={missingHistory}
							tagHistory={tagHistory}
							updateFunc={updateTagHistoryEntry}
							addTagAuditLog={addTagAuditLog}
						/>
					</Grid>
				)}
			</Grid>

			<TagDebugDialog
				open={debugOpen}
				onClose={() => setDebugOpen(false)}
				objectName={objectName}
				objectId={objectId}
			/>
		</Paper>
	);
};
